import React, { useEffect, useState } from "react";
import InputField from "components/form/InputField";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "components/form/Button";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import {
  updateTokenExpiredStatus,
  updateTokenStatus,
  useAccountDetails,
  userLogin,
} from "store/slices/authSlice";
import { useAppDispatch } from "store/hooks";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { onSetIsLogin } from "store/slices/commonSlice";
import { decode } from "utils/HelperFunctions";
import { SettingsValue } from "types/payments";
import { useTid } from "utils/testid";

const SignUpPasswordForm = () => {
  const tid = useTid();
  const initialValue = {
    email: "",
    password: "",
    isPasswordLogin: true,
  };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [values, setValues] = useState<any>(initialValue);
  const [isErr, setIsErr] = useState<boolean>(false);
  const accountDetails = useAccountDetails();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { state } = useLocation();

  const onSubmit = async (e: any) => {
    setIsLoading(true);
    e.preventDefault();
    let payload: any = { ...values };
    payload["clusterId"] = clusterId;
    try {
      const res: any = await dispatch(userLogin(payload)).unwrap();
      if (res && res?.body?.success) {
        localStorage.setItem(TOKEN_STORAGE_KEY, res?.body?.token);
        localStorage.setItem(TOKEN_STORAGE_TIME, `${new Date().getTime()}`);
        dispatch(updateTokenStatus(false));
        dispatch(updateTokenExpiredStatus(false));
        dispatch(onSetIsLogin(true));
        if (accountDetails?.data?.two_factor_auth === SettingsValue.YES) {
          navigate(`/${cluster_id}/otp-send`);
        } else {
          if (
            accountDetails?.data?.landing_page &&
            accountDetails?.data?.landing_page?.toLowerCase() === "payment"
          ) {
            navigate(`/${cluster_id}/make-payment`);
          } else if (
            accountDetails?.data?.landing_page &&
            accountDetails?.data?.landing_page?.toLowerCase() === "profile"
          ) {
            navigate(`/${cluster_id}/my-profile`);
          } else {
            navigate(`/${cluster_id}/dashboard`);
          }
        }
      } else {
        setIsErr(true);
      }
    } catch (error) {
      console.log("onSubmit error =====", error);
    } finally {
      setIsLoading(false);
    }
  };
  const checkIsDisabled = () => {
    return Object.values(values).every((value) => value === "");
  };

  useEffect(() => {
    if (state && state?.email) {
      setValues({ ...values, email: state?.email });
    }
  });

  return (
    <div
      className="animate__animated animate__fadeIn w-full lg:max-w-[520px]"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        Welcome - Secure Payment Portal
      </div>
      <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
        Please enter your email and password to access your account.
      </p>
      <form
        onSubmit={onSubmit}
        data-testid={tid("existing-account-login-form")}
      >
        <div className="max-w-[520px] flex flex-col lg:gap-[60px] gap-10">
          <div className="flex w-full flex-col lg:gap-6 gap-5">
            <InputField
              type="email"
              name="email"
              label="Email"
              data-testid={tid("email-input")}
              aria-label="Email address"
              placeholder="Enter your email address"
              value={values?.email}
              onChange={(e: any) => {
                setValues({
                  ...values,
                  email: e?.target?.value,
                });
              }}
              _className="!font-medium"
            />
            <span>
              <InputField
                type="password"
                name="password"
                label="Password"
                data-testid={tid("password-input")}
                aria-label="Password"
                placeholder="Enter your password"
                value={values?.password}
                onChange={(e: any) => {
                  setValues({
                    ...values,
                    password: e?.target?.value,
                  });
                }}
                _className="!font-medium"
              />
              <div className="flex justify-end">
                <Link
                  to={`/${cluster_id}/forgot-password`}
                  className="pt-1 cursor-pointer text-end text-blue hover:!text-btnPrimaryHover lg:text-base text-sm font-medium leading-normal underline"
                  data-testid={tid("forgot-password-link")}
                  aria-label="Forgot password"
                >
                  Forgot password?
                </Link>
              </div>
            </span>
          </div>

          <div className="w-full">
            <Button
              className="w-full"
              text="Login"
              type="submit"
              data-testid={tid("login-button")}
              aria-label="Log in to existing account"
              isDisabled={checkIsDisabled()}
              isLoading={isLoading}
              variant="variable"
            />
            {isErr && (
              <>
                <div
                  className="flex items-center gap-[5px] mt-3 px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                  role="alert"
                  data-testid={tid("signup-login-error-alert")}
                >
                  <InfoRedCircleIcon aria-hidden="true" />
                  <div className="lg:text-base text-sm text-red leading-[20.83px]">
                    Signup failed. Please check your credentials and try again.
                  </div>
                </div>
                {accountDetails?.data &&
                  accountDetails?.data.request_access === SettingsValue.YES && (
                    <div className="mt-4 text-iconBright lg:text-base text-sm leading-[20.83px] animate__animated animate__fadeIn">
                      <p>
                        It looks like this email isn't associated with an
                        account yet. If you're new here,{" "}
                        <Link
                          to={`/${cluster_id}/request-access`}
                          className="text-blue hover:!text-btnPrimaryHover"
                          data-testid={tid("request-access-link")}
                          aria-label="Request access"
                        >
                          request access
                        </Link>{" "}
                        to join us!
                      </p>
                    </div>
                  )}
              </>
            )}

            {!isErr && (
              <div className="max-w-[520px] flex flex-col lg:gap-4 gap-3 items-center pt-3 lg:pt-4">
                <Link
                  to={`/${cluster_id}/login`}
                  className="lg:text-base text-sm font-medium text-blue hover:!text-btnPrimaryHover leading-normal cursor-pointer"
                  data-testid={tid("use-data-login-link")}
                  aria-label="Validate your data instead"
                >
                  Validate your data instead
                </Link>
                <div className="h-[1px] w-full bg-strokePrimary" />
                <div className="flex gap-[6px] items-center justify-start w-full">
                  <p className="lg:text-base text-sm text-txtParagraph leading-normal">
                    Don't have an account?
                  </p>
                  <Link
                    to={`/${cluster_id}/sign-up`}
                    className="cursor-pointer lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium leading-normal"
                    data-testid={tid("sign-up-link")}
                    aria-label="Sign up"
                  >
                    Sign up
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default SignUpPasswordForm;
